import { token } from "@may-i/mash-design-system";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { toProcessableImageUrl } from "./downscale-image";

interface StepUploadProps {
  imageUrl: string | null;
  onImageChange: (url: string) => void;
  /** F2: 여러 파일이 선택되면 호출 — crop/split 건너뛰고 바로 선택 단계로 */
  onBatchFiles?: (files: File[]) => void;
}

export function StepUpload({ imageUrl, onImageChange, onBatchFiles }: StepUploadProps) {
  const { t } = useTranslation("resource");
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    // F2: 여러 파일이면 batch 콜백, 1개면 기존 단일 플로우
    if (imageFiles.length > 1 && onBatchFiles) {
      onBatchFiles(imageFiles);
    } else {
      // 초고해상도 사진은 2단계 원근보정(OpenCV) 메모리 초과를 유발하므로 처리 전 축소
      const url = await toProcessableImageUrl(imageFiles[0]);
      onImageChange(url);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    void handleFiles(e.target.files);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    void handleFiles(e.dataTransfer.files);
  };

  const handleReplaceClick = () => {
    inputRef.current?.click();
  };

  return (
    <Container>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleInputChange}
      />
      {imageUrl ? (
        <PreviewArea>
          <PreviewImage src={imageUrl} alt="uploaded" />
          <ReplaceOverlay onClick={handleReplaceClick} />
        </PreviewArea>
      ) : (
        <DropZone
          $isDragging={isDragging}
          onClick={() => inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <PlaceholderText>
            {t("multiCamera.addImagePlaceholder")}
          </PlaceholderText>
        </DropZone>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const DropZone = styled.div<{ $isDragging: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  border: 2px dashed
    ${({ $isDragging }) =>
      $isDragging ? token("colors.brand.primary") : token("colors.border.outline")};
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $isDragging }) =>
    $isDragging ? "rgba(0, 0, 0, 0.04)" : "transparent"};
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${token("colors.brand.primary")};
  }
`;

const PlaceholderText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;

const PreviewArea = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  background: #1b1c1f;
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const ReplaceOverlay = styled.div`
  position: absolute;
  inset: 0;
  cursor: pointer;
`;
